/**
 * Service for automatic audio synchronization using cross-correlation
 */
export class AudioSyncService {
  /**
   * Detect timing offset between two audio tracks using cross-correlation
   * This finds when the backing track audio appears in the recorded audio
   */
  static async detectTimingOffset(
    recordedAudioBlob: Blob,
    backingTrackBlob: Blob,
    maxOffsetSeconds: number = 10
  ): Promise<number> {
    try {
      console.log('Starting timing offset detection...');

      const audioContext = new AudioContext();

      const [recordedBuffer, backingBuffer] = await Promise.all([
        recordedAudioBlob.arrayBuffer().then(ab => audioContext.decodeAudioData(ab)),
        backingTrackBlob.arrayBuffer().then(ab => audioContext.decodeAudioData(ab))
      ]);

      const sampleRate = recordedBuffer.sampleRate;
      const maxOffsetSamples = Math.floor(maxOffsetSeconds * sampleRate);

      const recordedData = recordedBuffer.getChannelData(0);
      const backingData = backingBuffer.getChannelData(0);

      const searchWindowSamples = Math.min(
        Math.floor(5 * sampleRate),
        backingData.length
      );

      let bestOffset = 0;
      let bestCorrelation = -Infinity;

      console.log('Searching for best alignment...');
      const step = Math.floor(sampleRate * 0.01);

      for (let offset = -maxOffsetSamples; offset < maxOffsetSamples; offset += step) {
        const correlation = this.calculateCorrelation(
          recordedData,
          backingData,
          offset,
          searchWindowSamples
        );

        if (correlation > bestCorrelation) {
          bestCorrelation = correlation;
          bestOffset = offset;
        }
      }

      const fineStep = Math.floor(sampleRate * 0.001);
      const fineSearchRange = Math.floor(sampleRate * 0.1);

      for (let offset = bestOffset - fineSearchRange; offset <= bestOffset + fineSearchRange; offset += fineStep) {
        if (offset < -maxOffsetSamples || offset >= maxOffsetSamples) continue;

        const correlation = this.calculateCorrelation(
          recordedData,
          backingData,
          offset,
          searchWindowSamples
        );

        if (correlation > bestCorrelation) {
          bestCorrelation = correlation;
          bestOffset = offset;
        }
      }

      const offsetSeconds = bestOffset / sampleRate;

      console.log('Timing offset detection complete:', {
        offsetSamples: bestOffset,
        offsetSeconds,
        correlation: bestCorrelation
      });

      await audioContext.close();

      return offsetSeconds;

    } catch (error) {
      console.error('Error detecting timing offset:', error);
      return 0;
    }
  }

  /**
   * Calculate cross-correlation between two audio signals at a given offset
   */
  private static calculateCorrelation(
    signal1: Float32Array,
    signal2: Float32Array,
    offset: number,
    windowSize: number
  ): number {
    let sum = 0;
    let count = 0;

    for (let i = 0; i < windowSize; i++) {
      const index1 = i;
      const index2 = i + offset;

      if (index1 >= 0 && index1 < signal1.length &&
          index2 >= 0 && index2 < signal2.length) {
        sum += signal1[index1] * signal2[index2];
        count++;
      }
    }

    return count > 0 ? sum / count : 0;
  }

  /**
   * Apply timing offset to audio buffer
   */
  static async applyTimingOffset(
    audioBlob: Blob,
    offsetSeconds: number
  ): Promise<Blob> {
    try {
      if (Math.abs(offsetSeconds) < 0.001) {
        return audioBlob;
      }

      console.log('Applying timing offset:', offsetSeconds, 'seconds');

      const audioContext = new AudioContext();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const sampleRate = audioBuffer.sampleRate;
      const offsetSamples = Math.floor(Math.abs(offsetSeconds) * sampleRate);

      let newBuffer: AudioBuffer;

      if (offsetSeconds > 0) {
        const newLength = audioBuffer.length + offsetSamples;
        newBuffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          newLength,
          sampleRate
        );

        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
          const originalData = audioBuffer.getChannelData(channel);
          const newData = newBuffer.getChannelData(channel);

          for (let i = 0; i < offsetSamples; i++) {
            newData[i] = 0;
          }

          for (let i = 0; i < originalData.length; i++) {
            newData[i + offsetSamples] = originalData[i];
          }
        }
      } else {
        const newLength = Math.max(1, audioBuffer.length - offsetSamples);
        newBuffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          newLength,
          sampleRate
        );

        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
          const originalData = audioBuffer.getChannelData(channel);
          const newData = newBuffer.getChannelData(channel);

          for (let i = 0; i < newLength; i++) {
            const sourceIndex = i + offsetSamples;
            newData[i] = sourceIndex < originalData.length ? originalData[sourceIndex] : 0;
          }
        }
      }

      const mixedBlob = await this.audioBufferToBlob(newBuffer, audioContext);
      await audioContext.close();

      return mixedBlob;

    } catch (error) {
      console.error('Error applying timing offset:', error);
      return audioBlob;
    }
  }

  /**
   * Convert AudioBuffer to Blob
   */
  private static async audioBufferToBlob(
    audioBuffer: AudioBuffer,
    audioContext: AudioContext
  ): Promise<Blob> {
    return new Promise((resolve) => {
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;

      const destination = audioContext.createMediaStreamDestination();
      source.connect(destination);
      source.start(0);

      const mediaRecorder = new MediaRecorder(destination.stream);
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm; codecs=opus' });
        resolve(blob);
      };

      mediaRecorder.start();

      setTimeout(() => {
        if (mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
      }, audioBuffer.duration * 1000 + 100);
    });
  }
}
